import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { Organisation, OrganisationType } from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Tabs } from 'view/components/tabs'
import { OrganisationDetailsPanel } from './OrganisationDetailsPanel'
import { SalesCoveragePanel } from './SalesCoveragePanel'
import { Locale, Translation } from 'locale'
import { createOrganisationByIdGetterSelector } from 'selectors/organisationSelectors'
import { noop } from 'lodash'
import { createPlatformMarketStatusClosedSelector } from 'selectors/platformSelectors'
import { ViewPrimeBrokersPanel } from './ViewPrimeBrokersPanel'
import { ViewTradeSplitPanel } from './ViewTradeSplitPanel'
import { GfidPanelComponent } from './GfidPanel'

export interface ViewOrganisationDialogProps {
  // Own Props
  organisationEntityId: string
  onClose?: () => unknown

  // State Props
  getOrganisationEntity: (organisationId: string) => Organisation
  marketStatusClosed: boolean
}

const mapStateToProps = createStructuredSelector({
  getOrganisationEntity: createOrganisationByIdGetterSelector,
  marketStatusClosed: createPlatformMarketStatusClosedSelector()
})

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  width: 1000px;
  min-height: 450px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
`

export const ViewOrganisationDialogComponent: React.FunctionComponent<ViewOrganisationDialogProps> = props => {
  const { organisationEntityId, getOrganisationEntity, onClose, marketStatusClosed } = props

  const [selectedTab, setSelectedTab] = useState<Translation>(Translation.TC_ORGANISATION_DETAILS)

  const organisation = getOrganisationEntity(organisationEntityId)
  const title = organisation.legalName

  const getTabs = () => {
    const tabs = [Translation.TC_ORGANISATION_DETAILS, Translation.TC_ORGANISATION_PRIME_BROKERS, Translation.TC_GFID]

    if (organisation.organisationTypes.includes(OrganisationType.BUY_SIDE)) {
      tabs.push(Translation.TC_ORGANISATION_TRADE_SPLITS)
    }
    if (organisation.organisationTypes.includes(OrganisationType.SELL_SIDE)) {
      tabs.push(Translation.TC_ORGANISATION_SALES_COVERAGE)
    }

    return tabs
  }

  const handleChangeTab = (tab: Translation) => {
    setSelectedTab(tab)
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <ModalWindow title={title}>
      <Tabs selectedTab={selectedTab} onChangeTab={handleChangeTab} tabs={getTabs()} />
      <Content>
        {selectedTab === Translation.TC_ORGANISATION_DETAILS && (
          <OrganisationDetailsPanel
            organisation={organisation}
            validationIssues={[]}
            readOnly={true}
            onUpdate={noop}
            marketStatusClosed={marketStatusClosed}
          />
        )}
        {selectedTab === Translation.TC_GFID && (
          <GfidPanelComponent organisation={organisation} onUpdate={noop} readOnly={true} validationIssues={[]} />
        )}
        {selectedTab === Translation.TC_ORGANISATION_PRIME_BROKERS && <ViewPrimeBrokersPanel organisationId={organisation.id} />}
        {selectedTab === Translation.TC_ORGANISATION_TRADE_SPLITS && <ViewTradeSplitPanel organisationId={organisation.id} />}
        {selectedTab === Translation.TC_ORGANISATION_SALES_COVERAGE && <SalesCoveragePanel organisationId={organisation.id} />}
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'OrganisationDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'OrganisationDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={true}
              onClick={noop}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ViewOrganisationDialog = connect(mapStateToProps)(ViewOrganisationDialogComponent)
